import { http } from './config/config-receituario'

const jsonHeaders = (token) => ({
  Authorization: token,
  'Content-Type': 'application/json',
})

const cleanParams = (params = {}) =>
  Object.keys(params).reduce((query, key) => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value
    }
    return query
  }, {})

const request = (token, config) => {
  return http({
    ...config,
    params: cleanParams(config.params),
    headers: {
      ...jsonHeaders(token),
      ...(config.headers || {}),
    },
  }).then((response) => {
    if (response.data && response.data.sucesso === false) {
      const error = new Error(
        response.data.aviso && response.data.aviso.mensagem
          ? response.data.aviso.mensagem
          : response.data.mensagem || 'Nao foi possivel concluir a operacao.'
      )
      error.response = response
      error.businessWarning = true
      throw error
    }
    return response
  })
}

const listReceivables = (token, filters = {}) =>
  request(token, {
    method: 'get',
    url: 'contas',
    params: {
      ...filters,
      tipo: 'RECEBER',
    },
  })

const getReceivable = (token, id) =>
  request(token, {
    method: 'get',
    url: `contas/${id}`,
  })

const createReceivable = (token, payload) =>
  request(token, {
    method: 'post',
    url: 'contas',
    data: {
      ...payload,
      tipo_operacao: 'RECEBER',
    },
  })

const updateReceivable = (token, id, payload) =>
  request(token, {
    method: 'put',
    url: `contas/${id}`,
    data: payload,
  })

const deleteAccount = (token, id) =>
  request(token, {
    method: 'delete',
    url: `contas/${id}`,
  })

const listAttachments = (token, id) =>
  request(token, {
    method: 'get',
    url: `contas/${id}/anexos`,
  })

const uploadAttachments = (token, id, arquivos = []) => {
  const formData = new FormData()
  const files = Array.isArray(arquivos) ? arquivos : [arquivos]
  files.forEach((arquivo) => {
    if (arquivo) formData.append('arquivo', arquivo)
  })

  return request(token, {
    method: 'post',
    url: `contas/${id}/anexos`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const deleteAttachment = (token, id, attachmentId) =>
  request(token, {
    method: 'delete',
    url: `contas/${id}/anexos/${attachmentId}`,
  })

const getReceivableSummary = (token, filters = {}) =>
  request(token, {
    method: 'get',
    url: 'contas/receber/resumo',
    params: filters,
  })

const getClientBillingHistory = (token, clientId, filters = {}) =>
  request(token, {
    method: 'get',
    url: `contas/receber/historico/${clientId}`,
    params: filters,
  })

const listPayables = (token, filters = {}) =>
  request(token, {
    method: 'get',
    url: 'contas/pagar',
    params: filters,
  })

const getPayable = (token, id) =>
  request(token, {
    method: 'get',
    url: `contas/pagar/${id}`,
  })

const createPayable = (token, payload) =>
  request(token, {
    method: 'post',
    url: 'contas/pagar',
    data: payload,
  })

const updatePayable = (token, id, payload) =>
  request(token, {
    method: 'put',
    url: `contas/pagar/${id}`,
    data: payload,
  })

const getPayableSummary = (token, filters = {}) =>
  request(token, {
    method: 'get',
    url: 'contas/pagar/resumo',
    params: filters,
  })

const getSupplierPaymentHistory = (token, supplierId, filters = {}) =>
  request(token, {
    method: 'get',
    url: `contas/pagar/historico/${supplierId}`,
    params: filters,
  })

const getCashFlow = (token, month) =>
  request(token, {
    method: 'get',
    url: `contas/flow-account/${month}`,
  })

const listAccountsForReconciliation = (token, params = {}) =>
  request(token, {
    method: 'get',
    url: 'contas',
    params: {
      conta_bancaria: params.bankAccountId,
      data_inicio: params.startDate,
      data_fim: params.endDate,
      include_cancelados: false,
      sortBy: 'data_vencimento',
      sortOrder: 'ASC',
      take: 100,
    },
  })

const reconcileAccount = (token, params = {}) => {
  const payload = {
    pago: true,
    status: params.operation === 'PAGAR' ? 'pago' : 'recebido',
    data_recebimento: params.paymentDate,
    id_tipo_conta: params.bankAccountId,
  }

  return request(token, {
    method: 'put',
    url:
      params.operation === 'PAGAR'
        ? `contas/pagar/${params.accountId}`
        : `contas/${params.accountId}`,
    data: payload,
  })
}

const importBankStatement = (token, params = {}) => {
  const formData = new FormData()
  formData.append('arquivo', params.file)
  formData.append('id_tipo_conta', params.bankAccountId)
  if (params.startDate) formData.append('data_inicio', params.startDate)
  if (params.endDate) formData.append('data_fim', params.endDate)

  return request(token, {
    method: 'post',
    url: 'conciliacao-bancaria/importar',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const listBankReconciliation = (token, params = {}) =>
  request(token, {
    method: 'get',
    url: 'conciliacao-bancaria',
    params: {
      id_tipo_conta: params.bankAccountId,
      data_inicio: params.startDate,
      data_fim: params.endDate,
      status: params.status,
    },
  })

const listBankReconciliationCandidates = (token, transactionId) =>
  request(token, {
    method: 'get',
    url: `conciliacao-bancaria/transacoes/${transactionId}/candidatos`,
  })

const confirmBankReconciliation = (token, transactionId, payload) =>
  request(token, {
    method: 'post',
    url: `conciliacao-bancaria/transacoes/${transactionId}/conciliar`,
    data: payload,
  })

const ignoreBankReconciliationTransaction = (token, transactionId, payload = {}) =>
  request(token, {
    method: 'post',
    url: `conciliacao-bancaria/transacoes/${transactionId}/ignorar`,
    data: payload,
  })

const reopenBankReconciliationTransaction = (token, transactionId) =>
  request(token, {
    method: 'post',
    url: `conciliacao-bancaria/transacoes/${transactionId}/reabrir`,
  })

const createAccountFromBankTransaction = (token, transactionId, payload) =>
  request(token, {
    method: 'post',
    url: `conciliacao-bancaria/transacoes/${transactionId}/criar-lancamento`,
    data: payload,
  })

const reconcileSuggestedBankTransactions = (token, payload) =>
  request(token, {
    method: 'post',
    url: 'conciliacao-bancaria/conciliar-sugeridas',
    data: payload,
  })

export default {
  listReceivables,
  getReceivable,
  createReceivable,
  updateReceivable,
  deleteAccount,
  listAttachments,
  uploadAttachments,
  deleteAttachment,
  getReceivableSummary,
  getClientBillingHistory,
  listPayables,
  getPayable,
  createPayable,
  updatePayable,
  getPayableSummary,
  getSupplierPaymentHistory,
  getCashFlow,
  listAccountsForReconciliation,
  reconcileAccount,
  importBankStatement,
  listBankReconciliation,
  listBankReconciliationCandidates,
  confirmBankReconciliation,
  ignoreBankReconciliationTransaction,
  reopenBankReconciliationTransaction,
  createAccountFromBankTransaction,
  reconcileSuggestedBankTransactions,
  listar: (token, tipo = null, skip, take) => {
    const filters = {
      skip,
      take,
    }
    return tipo === 'PAGAR'
      ? listPayables(token, filters)
      : listReceivables(token, filters)
  },
  findAllFlowAccount: getCashFlow,
  salvar: (token, conta) =>
    conta && conta.tipo_operacao === 'PAGAR'
      ? createPayable(token, conta)
      : createReceivable(token, conta),
  atualizarConta: (token, conta) =>
    conta && conta.tipo_operacao === 'PAGAR'
      ? updatePayable(token, conta.id, conta)
      : updateReceivable(token, conta.id, conta),
  deleteConta: deleteAccount,
}
